import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import ViewField from "@/components/Input/ViewField";
import CardsList from "@/components/CardsList";
import Button from "@mui/material/Button";
// import NotesSection from "@/components/NotesSection";
import AttachmentsContainer from "@/components/AttachmentsContainer";
import VLFModal from "@/components/Modal";
import HandlePlayerModal from "@/pages/Scouting/Matches/Match/handlePlayer";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import {MatchAPI} from "@/api/match";
import {AttachmentAPI} from "@/api/attachment";

import {
	dateFormat,
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
} from "@/utils/constants";
import {getAttachment} from "@/utils/functions";

import "./index.scss";

function ScoutingMatchPage(props) {
	const {handleNotification} = props;

	const navigate = useNavigate();
	const {id} = useParams();
	const seasonId = useSelector((state) => state?.season?.id);

	const basePath = "/scouting/planning";

	const [loading, setLoading] = useState(false);
	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);
	const [openPlayerModal, setOpenPlayerModal] = useState(false);

	const [matchData, setMatchData] = useState({});
	// const [matchNotes, setMatchNotes] = useState([]);
	const [attachments, setAttachments] = useState([]);

	const [entityToDelete, setEntityToDelete] = useState({});

	const getMatch = async () => {
		setLoading(true);
		try {
			const response = await MatchAPI.getById(id, {seasonId});
			setMatchData(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	// const getMatchNotes = async () => {
	// 	try {
	// 		const response = await MatchAPI.getNotes(id);
	// 		setMatchNotes(response);
	// 	} catch (error) {
	// 		handleNotification(ERROR_OPERATION, "error");
	// 	}
	// };

	const getMatchAttachment = async () => {
		try {
			const response = await AttachmentAPI.list("match", id);
			setAttachments(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	useEffect(() => {
		getMatch();
		// getMatchNotes();
		getMatchAttachment();
	}, []);

	const deleteMatch = async () => {
		setLoading(true);
		try {
			await MatchAPI.delete(matchData?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate(basePath);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const deleteAttachment = async () => {
		setLoading(true);
		try {
			await AttachmentAPI.delete(entityToDelete?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenHandleConfirm(false);
			getMatchAttachment();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleDownload = async (id) => {
		setLoading(true);
		try {
			await getAttachment(id, attachments);
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleNote = async (data, id) => {
		setLoading(true);
		try {
			const body = {
				date: data?.date || moment().format("YYYY-MM-DD"),
				matchObservedId: matchData?.id,
				...data,
			};
			delete body.player;

			id
				? await MatchAPI.updateNote(matchData?.id, id, body)
				: await MatchAPI.createNote(body);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getMatchNotes();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteNote = async (id) => {
		setLoading(true);
		try {
			await MatchAPI.deleteNote(matchData?.id, id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getMatchNotes();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handlePlayer = async (data) => {
		setLoading(true);
		try {
			const body = {
				matchId: matchData?.id,
				...data,
			};

			await MatchAPI.createObservedPlayer(body);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getMatch();
			setOpenPlayerModal(false);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deletePlayer = async (id) => {
		setLoading(true);
		try {
			await MatchAPI.deleteObservedPlayer(matchData?.id, id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getMatch();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const getGoalsValue = (value) => {
		if (value !== null && value !== undefined) {
			return value;
		}

		return "---";
	};

	const matchConfiguration = [
		{
			value: matchData?.startDate
				? moment(matchData.startDate).format(dateFormat + " HH:mm")
				: null,
			label: "Data",
			size: "col-6 col-lg-4",
		},
		{
			value: matchData?.fieldName,
			label: "Luogo",
			size: "col-6 col-lg-4",
		},
		{
			value: matchData?.scoutNames,
			label: "Osservatori",
			size: "col-6 col-lg-4",
		},
		{
			value: matchData?.playersCategory,
			label: "Categoria",
			size: "col-6 col-lg-4",
		},
		{
			value: matchData?.homeClub,
			label: "Squadra casa",
			size: "col-6 col-lg-4",
		},
		{
			value: matchData?.awayClub,
			label: "Squadra ospite",
			size: "col-6 col-lg-4",
		},
		{
			value:
				(matchData?.homeClubGoals === null ||
					matchData?.homeClubGoals === undefined) &&
				(matchData?.awayClubGoals === null ||
					matchData?.awayClubGoals === undefined)
					? "---"
					: getGoalsValue(matchData?.homeClubGoals) +
						" - " +
						getGoalsValue(matchData?.awayClubGoals),
			label: "Risultato",
			size: "col-6 col-lg-4",
		},
	];

	matchConfiguration.push({
		value: matchData?.description,
		label: "Descrizione",
		size: "col-6 col-lg-4",
	});

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: () => navigate(basePath),
			color: "info",
		},
		{
			icon: <EditIcon />,
			title: "Modifica",
			onClick: () => navigate(basePath + "/" + matchData?.id + "/edit"),
		},
		{
			icon: <DeleteIcon />,
			title: "Elimina",
			onClick: () => {
				setEntityToDelete({type: "match", id});
				setOpenHandleConfirm(true);
			},
			color: "error",
		},
	];

	const breadcrumbsElements = [
		{label: "Partite", path: basePath},
		{label: "/"},
	];

	const listConfiguration = {
		data: matchData?.prospectsObserved?.map((player) => {
			return {
				...player.playerData,
				observedId: player.playerId,
			};
		}),
		onClick: (player) => navigate("/scouting/players/" + player.id),
	};

	return (
		<Page>
			<Loading visible={loading} />
			<div className="page-container">
				<Breadcrumbs
					elements={breadcrumbsElements}
					current={
						matchData?.startDate
							? matchData.homeClub + " - " + matchData.awayClub
							: ""
					}
					actions={<MainActions actions={mainActions} />}
				/>
				<div className="sheet-container">
					<div className="row no-margin col-container">
						<div className="col-sm-12 padding-small">
							<VLFCard
								title="Informazioni principali"
								content={
									<div className="row">
										<div className="col-sm-12 col-md-2">
											<div className="image-container generic">
												<img
													src={require("@/assets/images/coach/match.png")}
													alt=""
												/>
											</div>
										</div>
										<div className="col-sm-12 col-md-10">
											<ViewField configuration={matchConfiguration} />
										</div>
									</div>
								}
							/>
						</div>
						<div className="col-sm-12 no-padding mt-2">
							<div className="row no-margin mt-2">
								<div className="col-sm-12 padding-small">
									<div className="table-title px-1">Obiettivi</div>
									<div className="d-flex align-items-center justify-content-end mb-2">
										<Button
											variant="contained"
											onClick={() => {
												setOpenPlayerModal(true);
											}}
											size="small"
										>
											<div className="d-flex align-items-center">
												<div>Aggiungi</div>
											</div>
										</Button>
									</div>
									<CardsList
										type="scouting_database"
										handleDelete={deletePlayer}
										listConfiguration={listConfiguration}
										defaultSortedField="lastName"
										defaultSortedOrder="ascend"
									/>
								</div>
							</div>
						</div>
						{/* <div className="col-sm-12 no-padding mt-2">
							<NotesSection
								type="match"
								notes={matchNotes}
								prospect
								handleConfirm={handleNote}
								handleDelete={deleteNote}
							/>
						</div> */}
						<div className="col-sm-12 padding-small mt-2">
							<VLFCard
								title="Allegati"
								content={
									<AttachmentsContainer
										entityId={id}
										entityType="match"
										attachments={attachments}
										handleAdd={() => getMatchAttachment()}
										handleDownload={handleDownload}
										handleDelete={(id) => {
											setEntityToDelete({type: "attachment", id});
											setOpenHandleConfirm(true);
										}}
										handleNotification={handleNotification}
									/>
								}
							/>
						</div>
					</div>
				</div>
			</div>
			<VLFModal
				open={openHandleConfirm}
				title="Eliminazione"
				content={
					<div className="mt-2">
						Sei sicuro di voler eliminare questo elemento?
					</div>
				}
				confirm={() => {
					switch (entityToDelete?.type) {
						case "match":
							return deleteMatch();
						case "attachment":
							return deleteAttachment();
						default:
							return;
					}
				}}
				close={() => setOpenHandleConfirm(false)}
				confirmLabel="Conferma"
			/>
			{openPlayerModal && (
				<HandlePlayerModal
					open={openPlayerModal}
					confirm={handlePlayer}
					close={() => {
						setOpenPlayerModal(false);
					}}
					initialData={null}
				/>
			)}
		</Page>
	);
}

export default ScoutingMatchPage;
